import type { McpAuthType } from './types.js';

/**
 * One regional deployment of a vendor's MCP server. Vendors that host a
 * copy per region (Datadog calls them "sites") give each one its own
 * endpoint over its own tenant data, and they do not cross-route:
 * pointing at the wrong one authorizes cleanly and then answers about an
 * account the user does not have. So the catalog names them all instead
 * of shipping one host and hoping.
 *
 * Every host listed here was verified live before being added — an MCP
 * request answering 401 with metadata naming that host, over a
 * certificate covering it. A guessed regional hostname is worse than no
 * entry: it looks authoritative and fails at connect time.
 */
export interface McpCatalogRegion {
  /** The vendor's own identifier for the region, e.g. `'US1'`, `'EU1'`. */
  id: string;
  /** Picker label: the id plus the host it maps to, so it is checkable. */
  label: string;
  /** Streamable-HTTP endpoint serving this region. */
  endpoint: string;
}

/**
 * One entry in the built-in catalog of popular remote MCP servers.
 * Entries only *prefill* the add-server form — the endpoint, auth
 * method, and everything else stay editable before saving, so a vendor
 * moving its endpoint degrades to "user fixes the URL", never a broken
 * hard-coded flow.
 */
export interface McpCatalogEntry {
  /** Stable key, also used as the default config name. */
  slug: string;
  /** Display name (vendor branding). */
  name: string;
  /**
   * Streamable-HTTP endpoint of the vendor-hosted MCP server, and the
   * value the form prefills. For a multi-region vendor this is the
   * default pick and must also appear in {@link regions}.
   */
  endpoint: string;
  description: string;
  /**
   * Every region this vendor serves, when it serves more than one (see
   * {@link McpCatalogRegion}). Present only for multi-region vendors; a
   * single-host vendor leaves it undefined and the form shows no picker.
   */
  regions?: readonly McpCatalogRegion[];
  /**
   * The vendor's word for a region — "Site" for Datadog, "Region"
   * elsewhere — used as the picker's label. Only meaningful alongside
   * {@link regions}; defaults to "Region".
   */
  regionLabel?: string;
  /**
   * Suggested auth method. Most `'oauth'` entries publish RFC 9728
   * metadata and support the MCP authorization flow (dynamic client
   * registration + PKCE) — the same servers Cursor and Claude connect
   * with one click. A few (Slack, GitHub) still connect via `'oauth'`
   * but use a provider strategy their own plugin registers, which the
   * server auto-selects from the endpoint host (see `McpOAuthStrategy`).
   * `'bearer'` entries expect an API key / token. `'none'` entries are
   * public.
   */
  auth: McpAuthType;
  /** Vendor documentation for their MCP server. */
  docsUrl?: string;
  /**
   * One-line prerequisite the operator must satisfy on the vendor side
   * before the OAuth flow can succeed — shown next to the callback URL
   * in the add-server form. Only for vendors that gate the redirect URI
   * behind their own allowlist (see {@link mcpOAuthSetupNoteForEndpoint});
   * the common case needs nothing and stays undefined.
   *
   * This is static vendor data. A prerequisite that depends on what the
   * *deployment* wired up — "register this callback on your own OAuth
   * app" — belongs to the provider OAuth strategy in that vendor's
   * plugin instead, and reaches the form through
   * `GET /mcp-configs/oauth/providers`.
   */
  oauthSetupNote?: string;
}

/**
 * Popular vendor-hosted remote MCP servers, mirroring the pickers in
 * Cursor and Claude. Endpoints current as of August 2026; entries prefill
 * an editable form (see {@link McpCatalogEntry}), so drift is cheap to
 * correct. Order is the display order.
 */
export const MCP_SERVER_CATALOG: readonly McpCatalogEntry[] = [
  {
    slug: 'github',
    name: 'GitHub',
    endpoint: 'https://api.githubcopilot.com/mcp/',
    description: 'Repos, issues, pull requests, and code search.',
    // Connects via OAuth, but GitHub publishes authorization-server
    // metadata with no `registration_endpoint` — plugin-github registers
    // a provider strategy (deployment OAuth app + PKCE) for this host,
    // and the form's setup note comes from that strategy, not from here.
    auth: 'oauth',
    docsUrl: 'https://docs.github.com/en/copilot/customizing-copilot/using-the-github-mcp-server',
  },
  {
    slug: 'linear',
    name: 'Linear',
    endpoint: 'https://mcp.linear.app/mcp',
    description: 'Issues, projects, and cycles in Linear.',
    auth: 'oauth',
    docsUrl: 'https://linear.app/docs/mcp',
  },
  {
    slug: 'slack',
    name: 'Slack',
    endpoint: 'https://mcp.slack.com/mcp',
    description: 'Channels, DMs, canvases, and search across your Slack workspace.',
    // Connects via OAuth, but Slack's MCP endpoint doesn't support
    // dynamic client registration — the server auto-selects the 'slack'
    // strategy (deployment Slack app + user_scope) from this endpoint.
    auth: 'oauth',
    docsUrl: 'https://docs.slack.dev/tools/slack-mcp-server/',
  },
  {
    slug: 'notion',
    name: 'Notion',
    endpoint: 'https://mcp.notion.com/mcp',
    description: 'Search, read, and write Notion pages and databases.',
    auth: 'oauth',
    docsUrl: 'https://developers.notion.com/docs/mcp',
  },
  {
    slug: 'sentry',
    name: 'Sentry',
    endpoint: 'https://mcp.sentry.dev/mcp',
    description: 'Errors, issues, and performance data from Sentry.',
    auth: 'oauth',
    docsUrl: 'https://docs.sentry.io/product/sentry-mcp/',
  },
  {
    slug: 'honeycomb',
    name: 'Honeycomb',
    endpoint: 'https://mcp.honeycomb.io/mcp',
    description: 'Query traces, metrics, and SLOs in Honeycomb.',
    regions: [
      { id: 'US1', label: 'US1 — mcp.honeycomb.io', endpoint: 'https://mcp.honeycomb.io/mcp' },
      {
        id: 'EU1',
        label: 'EU1 — mcp.eu1.honeycomb.io',
        endpoint: 'https://mcp.eu1.honeycomb.io/mcp',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://docs.honeycomb.io/investigate/query/model-context-protocol/',
  },
  {
    slug: 'incident-io',
    name: 'incident.io',
    endpoint: 'https://mcp.incident.io/mcp',
    description: 'Incidents, alerts, on-call schedules, and escalations.',
    auth: 'oauth',
    docsUrl: 'https://docs.incident.io/ai/remote-mcp',
  },
  {
    slug: 'stripe',
    name: 'Stripe',
    endpoint: 'https://mcp.stripe.com',
    description: 'Customers, payments, subscriptions, and Stripe docs.',
    auth: 'oauth',
    docsUrl: 'https://docs.stripe.com/mcp',
  },
  {
    slug: 'vercel',
    name: 'Vercel',
    endpoint: 'https://mcp.vercel.com',
    description: 'Deployments, projects, and logs on Vercel.',
    auth: 'oauth',
    docsUrl: 'https://vercel.com/docs/mcp/vercel-mcp',
  },
  {
    slug: 'intercom',
    name: 'Intercom',
    endpoint: 'https://mcp.intercom.com/mcp',
    description: 'Conversations, contacts, and tickets in Intercom.',
    // Intercom hosts workspaces in US, EU, and Australia, but only the
    // first two answer on an MCP host today — there is no
    // mcp.au.* to point an Australian workspace at.
    regions: [
      { id: 'US', label: 'US — mcp.intercom.com', endpoint: 'https://mcp.intercom.com/mcp' },
      { id: 'EU', label: 'EU — mcp.eu.intercom.com', endpoint: 'https://mcp.eu.intercom.com/mcp' },
    ],
    auth: 'oauth',
    docsUrl: 'https://developers.intercom.com/docs/guides/mcp',
  },
  {
    slug: 'paypal',
    name: 'PayPal',
    endpoint: 'https://mcp.paypal.com/mcp',
    description: 'Invoices, payments, and disputes via PayPal.',
    auth: 'oauth',
    docsUrl: 'https://developer.paypal.com/tools/mcp-server/',
  },
  {
    slug: 'atlassian',
    name: 'Atlassian',
    endpoint: 'https://mcp.atlassian.com/v1/mcp',
    description: 'Jira issues and Confluence pages.',
    auth: 'oauth',
    docsUrl: 'https://support.atlassian.com/atlassian-rovo-mcp-server/',
  },
  {
    slug: 'hubspot',
    name: 'HubSpot',
    endpoint: 'https://mcp.hubspot.com',
    description: 'Contacts, companies, deals, and tickets in HubSpot CRM.',
    auth: 'oauth',
    docsUrl: 'https://developers.hubspot.com/mcp',
  },
  {
    slug: 'asana',
    name: 'Asana',
    endpoint: 'https://mcp.asana.com/v2/mcp',
    description: 'Tasks, projects, and goals in Asana.',
    auth: 'oauth',
    docsUrl: 'https://developers.asana.com/docs/using-asanas-mcp-server',
  },
  {
    slug: 'monday',
    name: 'monday.com',
    endpoint: 'https://mcp.monday.com/mcp',
    description: 'Boards, items, and updates in monday.com.',
    auth: 'oauth',
    docsUrl: 'https://developer.monday.com/api-reference/docs/build-on-monday-with-ai',
  },
  {
    slug: 'canva',
    name: 'Canva',
    endpoint: 'https://mcp.canva.com/mcp',
    description: 'Create, edit, and export designs in Canva.',
    auth: 'oauth',
    docsUrl: 'https://www.canva.dev/docs/mcp/',
  },
  {
    slug: 'webflow',
    name: 'Webflow',
    endpoint: 'https://mcp.webflow.com/mcp',
    description: 'Sites, CMS content, and pages in Webflow.',
    auth: 'oauth',
    docsUrl: 'https://developers.webflow.com/mcp/reference/how-it-works',
  },
  {
    slug: 'supabase',
    name: 'Supabase',
    endpoint: 'https://mcp.supabase.com/mcp',
    description: 'Projects, database queries, and logs in Supabase.',
    auth: 'oauth',
    docsUrl: 'https://supabase.com/docs/guides/getting-started/mcp',
  },
  {
    slug: 'neon',
    name: 'Neon',
    endpoint: 'https://mcp.neon.tech/mcp',
    description: 'Neon Postgres projects, branches, and SQL queries.',
    auth: 'oauth',
    docsUrl: 'https://neon.com/docs/ai/neon-mcp-server',
  },
  {
    slug: 'netlify',
    name: 'Netlify',
    endpoint: 'https://netlify-mcp.netlify.app/mcp',
    description: 'Deploys, sites, and env vars on Netlify.',
    auth: 'oauth',
    docsUrl: 'https://docs.netlify.com/build/build-with-ai/netlify-mcp-server/',
  },
  // These OAuth endpoints accepted Reflex's RFC 7591 public-client
  // registration metadata and HTTPS callback during live verification
  // on 2026-07-24.
  {
    slug: 'airtable',
    name: 'Airtable',
    endpoint: 'https://mcp.airtable.com/mcp',
    description: 'Bases, records, schemas, and comments in Airtable.',
    auth: 'oauth',
    docsUrl: 'https://support.airtable.com/docs/using-the-airtable-mcp-server',
  },
  {
    slug: 'amplitude',
    name: 'Amplitude',
    endpoint: 'https://mcp.amplitude.com/mcp',
    description: 'Product analytics, experiments, cohorts, and dashboards.',
    regions: [
      { id: 'US', label: 'US — mcp.amplitude.com', endpoint: 'https://mcp.amplitude.com/mcp' },
      {
        id: 'EU',
        label: 'EU — mcp.eu.amplitude.com',
        endpoint: 'https://mcp.eu.amplitude.com/mcp',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://amplitude.com/docs/amplitude-ai/amplitude-mcp',
  },
  {
    slug: 'gitlab',
    name: 'GitLab',
    endpoint: 'https://gitlab.com/api/v4/mcp',
    description: 'Projects, issues, merge requests, pipelines, and code.',
    auth: 'oauth',
    docsUrl: 'https://docs.gitlab.com/user/gitlab_duo/model_context_protocol/',
  },
  {
    slug: 'railway',
    name: 'Railway',
    endpoint: 'https://mcp.railway.com/',
    description: 'Projects, services, deployments, variables, and logs.',
    auth: 'oauth',
    docsUrl: 'https://docs.railway.com/ai/mcp-server',
  },
  {
    slug: 'wix',
    name: 'Wix',
    endpoint: 'https://mcp.wix.com/mcp',
    description: 'Sites, business data, apps, and Wix developer docs.',
    auth: 'oauth',
    docsUrl: 'https://dev.wix.com/docs/api-reference/articles/wix-mcp/about-the-wix-mcp',
  },
  {
    slug: 'grafana',
    name: 'Grafana Cloud',
    endpoint: 'https://mcp.grafana.com/mcp',
    description: 'Dashboards, metrics, logs, alerts, and incidents in Grafana Cloud.',
    auth: 'oauth',
    docsUrl:
      'https://grafana.com/docs/grafana-cloud/machine-learning/assistant/configure/cloud-mcp/',
  },
  {
    slug: 'miro',
    name: 'Miro',
    endpoint: 'https://mcp.miro.com/',
    description: 'Boards, diagrams, documents, tables, and comments.',
    auth: 'oauth',
    docsUrl: 'https://developers.miro.com/docs/miro-mcp',
  },
  {
    slug: 'todoist',
    name: 'Todoist',
    endpoint: 'https://ai.todoist.net/mcp',
    description: 'Tasks, projects, sections, labels, and comments.',
    auth: 'oauth',
    docsUrl: 'https://developer.todoist.com/',
  },
  {
    slug: 'mapbox',
    name: 'Mapbox',
    endpoint: 'https://mcp.mapbox.com/mcp',
    description: 'Geocoding, directions, maps, isochrones, and places.',
    auth: 'oauth',
    docsUrl: 'https://docs.mapbox.com/api/guides/mcp-server/',
  },
  {
    slug: 'prisma',
    name: 'Prisma',
    endpoint: 'https://mcp.prisma.io/mcp',
    description: 'Prisma Postgres databases, schemas, queries, and backups.',
    auth: 'oauth',
    docsUrl: 'https://docs.prisma.io/docs/ai/tools/mcp-server',
  },
  {
    slug: 'postman',
    name: 'Postman',
    endpoint: 'https://mcp.postman.com/minimal',
    description: 'Basic Postman operations through its OAuth-enabled US server.',
    auth: 'oauth',
    docsUrl:
      'https://learning.postman.com/docs/developer/postman-api/postman-mcp-server/postman-mcp-remote-server/',
  },
  {
    slug: 'datadog',
    name: 'Datadog',
    endpoint: 'https://mcp.datadoghq.com/v1/mcp',
    description: 'Metrics, logs, traces, monitors, and incidents in Datadog.',
    // Datadog runs one MCP server per site, each answering only for the
    // organizations on that site. Hosts verified against their RFC 9728
    // metadata on 2026-08-04; the GovCloud sites are excluded because
    // Datadog does not run the MCP server there.
    regionLabel: 'Site',
    regions: [
      { id: 'US1', label: 'US1 — mcp.datadoghq.com', endpoint: 'https://mcp.datadoghq.com/v1/mcp' },
      {
        id: 'US3',
        label: 'US3 — mcp.us3.datadoghq.com',
        endpoint: 'https://mcp.us3.datadoghq.com/v1/mcp',
      },
      {
        id: 'US5',
        label: 'US5 — mcp.us5.datadoghq.com',
        endpoint: 'https://mcp.us5.datadoghq.com/v1/mcp',
      },
      { id: 'EU1', label: 'EU1 — mcp.datadoghq.eu', endpoint: 'https://mcp.datadoghq.eu/v1/mcp' },
      {
        id: 'AP1',
        label: 'AP1 — mcp.ap1.datadoghq.com',
        endpoint: 'https://mcp.ap1.datadoghq.com/v1/mcp',
      },
      {
        id: 'AP2',
        label: 'AP2 — mcp.ap2.datadoghq.com',
        endpoint: 'https://mcp.ap2.datadoghq.com/v1/mcp',
      },
      {
        id: 'UK1',
        label: 'UK1 — mcp.uk1.datadoghq.com',
        endpoint: 'https://mcp.uk1.datadoghq.com/v1/mcp',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://docs.datadoghq.com/mcp_server/',
    // Datadog's registration endpoint hands every caller the same
    // client id and ignores the redirect URIs it is sent; authorization
    // is validated against a per-organization allowlist instead, so an
    // un-allowlisted callback fails at the authorize step with
    // "Mismatching redirect URI".
    oauthSetupNote:
      'Datadog only accepts allowlisted callback URLs. Add this one in Datadog under Organization Settings → Preferences → MCP OAuth Redirect URLs before connecting.',
  },
  {
    slug: 'cloudflare',
    name: 'Cloudflare',
    endpoint: 'https://mcp.cloudflare.com/mcp',
    description: 'Workers, storage, DNS, security, and the Cloudflare API.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cloudflare/mcp',
  },
  // The vendor-hosted servers below published RFC 9728 protected-resource
  // metadata and an OAuth registration endpoint during live verification
  // on 2026-08-11. Each also appears in Cursor's verified marketplace,
  // Claude's connector directory, or the vendor's official agent plugin.
  {
    slug: 'antimetal',
    name: 'Antimetal',
    endpoint: 'https://mcp.antimetal.com',
    description: 'Cloud costs, usage, and optimization recommendations.',
    auth: 'oauth',
    docsUrl: 'https://github.com/antimetal/cursor-plugin',
  },
  {
    slug: 'apify',
    name: 'Apify',
    endpoint: 'https://mcp.apify.com',
    description: 'Actors, web scraping, datasets, and automation runs.',
    auth: 'oauth',
    docsUrl: 'https://docs.apify.com/platform/integrations/mcp',
  },
  {
    slug: 'apollo',
    name: 'Apollo.io',
    endpoint: 'https://mcp.apollo.io/mcp',
    description: 'Contacts, companies, sequences, and sales intelligence.',
    auth: 'oauth',
    docsUrl: 'https://github.com/apolloio/apollo-mcp-plugin',
  },
  {
    slug: 'ashby',
    name: 'Ashby',
    endpoint: 'https://mcp.ashbyhq.com/mcp/v1',
    description: 'Candidates, jobs, interviews, and recruiting workflows.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cursor/plugins/tree/main/third_party/ashby',
  },
  {
    slug: 'atlan',
    name: 'Atlan',
    endpoint: 'https://mcp.atlan.com/mcp',
    description: 'Data catalog assets, lineage, governance, and discovery.',
    auth: 'oauth',
    docsUrl: 'https://github.com/atlanhq/agent-toolkit',
  },
  {
    slug: 'braintrust',
    name: 'Braintrust',
    endpoint: 'https://api.braintrust.dev/mcp',
    description: 'AI traces, experiments, datasets, and evaluations.',
    auth: 'oauth',
    docsUrl: 'https://github.com/braintrustdata/braintrust-cursor-extension',
  },
  {
    slug: 'buildkite',
    name: 'Buildkite',
    endpoint: 'https://mcp.buildkite.com/mcp',
    description: 'Pipelines, builds, jobs, tests, and logs.',
    auth: 'oauth',
    docsUrl: 'https://buildkite.com/docs/apis/mcp-server',
  },
  {
    slug: 'chainguard',
    name: 'Chainguard',
    endpoint: 'https://console-api.enforce.dev/mcp',
    description: 'Container images, packages, vulnerabilities, and account data.',
    auth: 'oauth',
    docsUrl: 'https://github.com/chainguard-dev/chainguard-cursor-plugin',
  },
  {
    slug: 'chatprd',
    name: 'ChatPRD',
    endpoint: 'https://app.chatprd.ai/mcp',
    description: 'Product requirements, roadmaps, and product strategy.',
    auth: 'oauth',
    docsUrl: 'https://github.com/ChatPRD/cursor-plugin',
  },
  {
    slug: 'circleback',
    name: 'Circleback',
    endpoint: 'https://circleback.ai/api/mcp',
    description: 'Meeting transcripts, notes, action items, and contacts.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cursor/plugins/tree/main/third_party/circleback',
  },
  {
    slug: 'clay',
    name: 'Clay',
    endpoint: 'https://api.clay.com/v3/mcp',
    description: 'People and company enrichment plus Clay workflows.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cursor/plugins/tree/main/third_party/clay',
  },
  {
    slug: 'clickup',
    name: 'ClickUp',
    endpoint: 'https://mcp.clickup.com/mcp',
    description: 'Tasks, docs, lists, and workspace management.',
    auth: 'oauth',
    docsUrl: 'https://github.com/clickup/clickup-plugin',
  },
  {
    slug: 'cloudinary',
    name: 'Cloudinary',
    endpoint: 'https://asset-management.mcp.cloudinary.com/mcp',
    description: 'Media assets, metadata, transformations, and delivery.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cloudinary-devs/cloudinary-plugin',
  },
  {
    slug: 'cockroachdb',
    name: 'CockroachDB Cloud',
    endpoint: 'https://cockroachlabs.cloud/mcp',
    description: 'Clusters, databases, SQL insights, and operations.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cockroachdb/cursor-plugin',
  },
  {
    slug: 'confidence',
    name: 'Confidence',
    endpoint: 'https://mcp.confidence.dev/mcp/flags',
    description: 'Feature flags, targeting, and rollout management.',
    auth: 'oauth',
    docsUrl: 'https://github.com/spotify/confidence-ai-plugins',
  },
  {
    slug: 'exa',
    name: 'Exa',
    endpoint: 'https://mcp.exa.ai/mcp',
    description: 'Web search, research, and page content extraction.',
    auth: 'oauth',
    docsUrl: 'https://github.com/exa-labs/exa-cursor-plugin',
  },
  {
    slug: 'figma',
    name: 'Figma',
    endpoint: 'https://mcp.figma.com/mcp',
    description: 'Design files, components, variables, and design-to-code context.',
    auth: 'oauth',
    docsUrl: 'https://github.com/figma/mcp-server-guide',
  },
  {
    slug: 'granola',
    name: 'Granola',
    endpoint: 'https://mcp.granola.ai/mcp',
    description: 'Meeting notes, transcripts, and action items.',
    auth: 'oauth',
    docsUrl: 'https://github.com/granola-inc/granola-cursor-plugin',
  },
  {
    slug: 'harness',
    name: 'Harness',
    endpoint: 'https://mcp.harness.io/mcp',
    description: 'Pipelines, services, deployments, and platform resources.',
    auth: 'oauth',
    docsUrl: 'https://github.com/harness/harness-ai',
  },
  {
    slug: 'hex',
    name: 'Hex',
    endpoint: 'https://app.hex.tech/mcp',
    description: 'Projects, notebooks, data connections, and analytics.',
    auth: 'oauth',
    docsUrl: 'https://github.com/hex-inc/hex-cursor-plugin',
  },
  {
    slug: 'heygen',
    name: 'HeyGen',
    endpoint: 'https://mcp.heygen.com/mcp/v1/',
    description: 'Avatars, videos, translations, and media generation.',
    auth: 'oauth',
    docsUrl: 'https://github.com/heygen-com/skills',
  },
  {
    slug: 'higgsfield',
    name: 'Higgsfield',
    endpoint: 'https://mcp.higgsfield.ai/mcp',
    description: 'AI image and video generation workflows.',
    auth: 'oauth',
    docsUrl: 'https://github.com/higgsfield-ai/cursor-plugin',
  },
  {
    slug: 'launchdarkly',
    name: 'LaunchDarkly',
    endpoint: 'https://mcp.launchdarkly.com/mcp/fm',
    description: 'Feature flags, projects, environments, and targeting.',
    auth: 'oauth',
    docsUrl: 'https://github.com/launchdarkly/agent-skills',
  },
  {
    slug: 'lightfield',
    name: 'Lightfield',
    endpoint: 'https://mcp.lightfield.app/mcp',
    description: 'Accounts, contacts, opportunities, notes, tasks, and meetings in Lightfield CRM.',
    auth: 'oauth',
    docsUrl: 'https://docs.lightfield.app/getting-started/mcp-quickstart/',
  },
  {
    slug: 'magic-patterns',
    name: 'Magic Patterns',
    endpoint: 'https://mcp.magicpatterns.com/mcp',
    description: 'UI designs, prototypes, and component generation.',
    auth: 'oauth',
    docsUrl: 'https://github.com/magicpatterns/agent-plugins',
  },
  {
    slug: 'mem0',
    name: 'Mem0',
    endpoint: 'https://mcp.mem0.ai/mcp/',
    description: 'Persistent agent memories and semantic memory search.',
    auth: 'oauth',
    docsUrl: 'https://github.com/mem0ai/mem0',
  },
  {
    slug: 'mintlify',
    name: 'Mintlify',
    endpoint: 'https://mcp.mintlify.com',
    description: 'Documentation search, updates, and project context.',
    auth: 'oauth',
    docsUrl: 'https://github.com/mintlify/cursor-plugin',
  },
  {
    slug: 'mixpanel',
    name: 'Mixpanel',
    endpoint: 'https://mcp.mixpanel.com/mcp',
    description: 'Product analytics, funnels, retention, cohorts, and experiments.',
    regions: [
      { id: 'US', label: 'US — mcp.mixpanel.com', endpoint: 'https://mcp.mixpanel.com/mcp' },
      { id: 'EU', label: 'EU — mcp-eu.mixpanel.com', endpoint: 'https://mcp-eu.mixpanel.com/mcp' },
      {
        id: 'IN',
        label: 'India — mcp-in.mixpanel.com',
        endpoint: 'https://mcp-in.mixpanel.com/mcp',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://docs.mixpanel.com/docs/mcp',
  },
  {
    slug: 'navan',
    name: 'Navan',
    endpoint: 'https://mcp.navan.com/mcp',
    description: 'Travel bookings, policies, approvals, and corporate cards.',
    auth: 'oauth',
    docsUrl: 'https://github.com/cursor/plugins/tree/main/third_party/navan',
  },
  {
    slug: 'opsera',
    name: 'Opsera',
    endpoint: 'https://agent.opsera.ai/mcp',
    description: 'CI/CD pipelines, security, and DevOps automation.',
    auth: 'oauth',
    docsUrl: 'https://github.com/opsera-agents/opsera-cursor',
  },
  {
    slug: 'pendo',
    name: 'Pendo',
    endpoint: 'https://app.pendo.io/mcp/v0/shttp',
    description: 'Product analytics, guides, feedback, and application data.',
    auth: 'oauth',
    docsUrl: 'https://github.com/pendo-io/cursor-pendo-plugin',
  },
  {
    slug: 'plain',
    name: 'Plain',
    endpoint: 'https://mcp.plain.com/mcp',
    description: 'Customer support threads, contacts, and workspace operations.',
    auth: 'oauth',
    docsUrl: 'https://github.com/team-plain/cursor-plugin-mcp',
  },
  {
    slug: 'posthog',
    name: 'PostHog',
    endpoint: 'https://mcp.posthog.com/mcp',
    description: 'Analytics, feature flags, experiments, errors, and recordings.',
    auth: 'oauth',
    docsUrl: 'https://github.com/PostHog/ai-plugin',
  },
  {
    slug: 'raisely',
    name: 'Raisely',
    endpoint: 'https://mcp.raisely.com/mcp',
    description: 'Fundraising campaigns, supporters, donations, and CRM data.',
    auth: 'oauth',
    docsUrl: 'https://github.com/raisely/cursor-plugin',
  },
  {
    slug: 'resend',
    name: 'Resend',
    endpoint: 'https://mcp.resend.com/mcp',
    description: 'Emails, domains, contacts, broadcasts, and API keys.',
    auth: 'oauth',
    docsUrl: 'https://github.com/resend/resend-skills',
  },
  {
    slug: 'sanity',
    name: 'Sanity',
    endpoint: 'https://mcp.sanity.io',
    description: 'Datasets, documents, schemas, and content operations.',
    auth: 'oauth',
    docsUrl: 'https://github.com/sanity-io/agent-toolkit',
  },
  {
    slug: 'thoughtspot',
    name: 'ThoughtSpot SpotterCode',
    endpoint: 'https://spottercode.thoughtspot.app/mcp',
    description: 'Analytics models, queries, and ThoughtSpot development.',
    auth: 'oauth',
    docsUrl: 'https://github.com/thoughtspot/spottercode-mcp-plugin',
  },
  {
    slug: 'fullstory',
    name: 'FullStory',
    endpoint: 'https://api.fullstory.com/mcp/subtext',
    description: 'Session data, user journeys, and behavioral insights.',
    regions: [
      {
        id: 'US',
        label: 'US — api.fullstory.com',
        endpoint: 'https://api.fullstory.com/mcp/subtext',
      },
      {
        id: 'EU1',
        label: 'EU1 — api.eu1.fullstory.com',
        endpoint: 'https://api.eu1.fullstory.com/mcp/subtext',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://github.com/fullstorydev/subtext',
  },
  {
    slug: 'tavily',
    name: 'Tavily',
    endpoint: 'https://mcp.tavily.com/mcp',
    description: 'Web search, extraction, crawling, and research.',
    auth: 'oauth',
    docsUrl: 'https://github.com/tavily-ai/tavily-cursor-plugin',
  },
  {
    slug: 'thousandeyes',
    name: 'ThousandEyes',
    endpoint: 'https://api.thousandeyes.com/mcp',
    description: 'Network tests, outages, agents, and telemetry.',
    auth: 'oauth',
    docsUrl: 'https://github.com/thousandeyes/thousandeyes-ai-agents-toolkit',
  },
  {
    slug: 'tierzero',
    name: 'Tier Zero',
    endpoint: 'https://api.tierzero.ai/mcp/',
    description: 'Production database queries and operational workflows.',
    auth: 'oauth',
    docsUrl: 'https://github.com/TierZeroAI/tierzero-cursor',
  },
  {
    slug: 'workos',
    name: 'WorkOS',
    endpoint: 'https://mcp.workos.com/mcp',
    description: 'Organizations, users, directories, and authentication settings.',
    auth: 'oauth',
    docsUrl: 'https://github.com/workos/skills',
  },
  {
    slug: 'zapier',
    name: 'Zapier',
    endpoint: 'https://mcp.zapier.com/api/v1/connect',
    description: 'Connected apps and user-configured automation actions.',
    auth: 'oauth',
    docsUrl: 'https://github.com/zapier/zapier-mcp',
  },
  {
    slug: 'hugging-face',
    name: 'Hugging Face',
    endpoint: 'https://huggingface.co/mcp',
    description: 'Models, datasets, papers, and Spaces on the Hub.',
    auth: 'oauth',
    docsUrl: 'https://huggingface.co/settings/mcp',
  },
  {
    slug: 'context7',
    name: 'Context7',
    endpoint: 'https://mcp.context7.com/mcp',
    description: 'Up-to-date library docs and code examples for prompts.',
    auth: 'bearer',
    docsUrl: 'https://context7.com',
  },
  {
    slug: 'deepwiki',
    name: 'DeepWiki',
    endpoint: 'https://mcp.deepwiki.com/mcp',
    description: 'Ask questions about any public GitHub repository.',
    auth: 'none',
    docsUrl: 'https://docs.devin.ai/work-with-devin/deepwiki-mcp',
  },
  {
    slug: 'cloudflare-docs',
    name: 'Cloudflare Docs',
    endpoint: 'https://docs.mcp.cloudflare.com/mcp',
    description: 'Search Cloudflare developer documentation.',
    auth: 'none',
    docsUrl: 'https://github.com/cloudflare/mcp-server-cloudflare',
  },
  {
    slug: 'aws',
    name: 'AWS',
    endpoint: 'https://aws-mcp.us-east-1.api.aws/mcp',
    description: 'Current AWS docs, guided workflows, and authenticated calls across AWS APIs.',
    // AWS operates the MCP server in exactly these two regions today
    // (docs.aws.amazon.com/general/latest/gr/aws-mcp.html) — this is the
    // complete list, not a partial one.
    regions: [
      {
        id: 'us-east-1',
        label: 'us-east-1 — aws-mcp.us-east-1.api.aws',
        endpoint: 'https://aws-mcp.us-east-1.api.aws/mcp',
      },
      {
        id: 'eu-central-1',
        label: 'eu-central-1 — aws-mcp.eu-central-1.api.aws',
        endpoint: 'https://aws-mcp.eu-central-1.api.aws/mcp',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://docs.aws.amazon.com/general/latest/gr/aws-mcp.html',
  },
  {
    slug: 'newrelic',
    name: 'New Relic',
    endpoint: 'https://mcp.newrelic.com/mcp/',
    description: 'Entities, dashboards, alerts, incidents, and logs in New Relic.',
    regions: [
      { id: 'US', label: 'US — mcp.newrelic.com', endpoint: 'https://mcp.newrelic.com/mcp/' },
      {
        id: 'EU',
        label: 'EU — mcp.eu.newrelic.com',
        endpoint: 'https://mcp.eu.newrelic.com/mcp/',
      },
    ],
    auth: 'oauth',
    docsUrl: 'https://docs.newrelic.com/docs/agentic-ai/mcp/overview/',
  },
  {
    slug: 'semgrep',
    name: 'Semgrep',
    endpoint: 'https://mcp.semgrep.ai/mcp',
    description: 'Static analysis scans for security and code-quality issues.',
    auth: 'oauth',
    docsUrl: 'https://github.com/semgrep/mcp',
  },
  {
    slug: 'socket',
    name: 'Socket',
    endpoint: 'https://mcp.socket.dev/',
    description: 'Package risk scores, dependency audits, and supply-chain alerts.',
    auth: 'oauth',
    docsUrl: 'https://docs.socket.dev/docs/remote-socket-mcp',
  },
] as const;

/**
 * Every host in the catalog, mapped to the entry that answers on it: an
 * entry's default host plus each of its regional hosts. Built once, because
 * the catalog is a module constant and the alternative is re-parsing ~100 URLs
 * per lookup — and callers look up once per registered server.
 */
const ENTRY_BY_HOST: ReadonlyMap<string, McpCatalogEntry> = new Map(
  MCP_SERVER_CATALOG.flatMap((entry) =>
    [entry.endpoint, ...(entry.regions?.map((r) => r.endpoint) ?? [])].map(
      (endpoint) => [new URL(endpoint).hostname, entry] as const,
    ),
  ),
);

/**
 * The catalog entry serving `endpoint`, matched on hostname across the
 * entry's default host and every regional host. Keyed by host rather
 * than by catalog pick so a hand-typed or path-edited endpoint still
 * resolves. Matching is exact, never suffix-based, so a lookalike host
 * outside the catalog matches nothing. Returns undefined for unknown
 * hosts and unparseable input.
 */
export function mcpCatalogEntryForEndpoint(endpoint: string): McpCatalogEntry | undefined {
  let host: string;
  try {
    host = new URL(endpoint).hostname;
  } catch {
    return undefined;
  }
  return ENTRY_BY_HOST.get(host);
}

/**
 * The name to register a catalog server under: the entry's `slug`, carrying
 * the region for a multi-region vendor.
 *
 * The server treats (org, name, owner) as an idempotency key and repoints the
 * existing row, so the region has to be in the name — without it, connecting
 * Datadog EU after Datadog US would silently swing everyone already using
 * `datadog` over to the other site's data rather than adding a second server.
 * {@link mcpCatalogEntryForName} is the inverse; keep the two together.
 */
export function mcpCatalogServerName(entry: McpCatalogEntry, region?: McpCatalogRegion): string {
  return region ? `${entry.slug}-${region.id.toLowerCase()}` : entry.slug;
}

/**
 * The catalog entry a server *name* refers to, or undefined for a name no
 * vendor owns.
 *
 * Two naming conventions feed this. The connect panel names the servers it
 * creates with {@link mcpCatalogServerName}, so this resolves a registered
 * config back to its vendor; and a user typing or picking a vendor spells its
 * display name ("incident.io"), so that matches too. Matching is exact apart
 * from the `slug-<region>` suffix, so "linearize" is not Linear.
 */
export function mcpCatalogEntryForName(name: string): McpCatalogEntry | undefined {
  const key = name.trim().toLowerCase();
  if (!key) return undefined;
  // Exact first. `cloudflare-docs` is its own vendor, not Cloudflare's `docs`
  // region, and the regional pass below would otherwise hand it to whichever
  // entry the catalog happens to list first.
  const exact = MCP_SERVER_CATALOG.find(
    (entry) => entry.slug.toLowerCase() === key || entry.name.toLowerCase() === key,
  );
  if (exact) return exact;
  // Only regions the vendor actually serves, because those are the only
  // suffixes {@link mcpCatalogServerName} ever writes. A hand-named
  // `linear-staging` is somebody's own server, not Linear's.
  return MCP_SERVER_CATALOG.find((entry) =>
    entry.regions?.some((region) => mcpCatalogServerName(entry, region).toLowerCase() === key),
  );
}

/**
 * The {@link McpCatalogEntry.oauthSetupNote} for `endpoint`, or
 * undefined when the vendor has no prerequisite. The note is a property
 * of the vendor, not of one host, so it follows every region the vendor
 * serves.
 */
export function mcpOAuthSetupNoteForEndpoint(endpoint: string): string | undefined {
  return mcpCatalogEntryForEndpoint(endpoint)?.oauthSetupNote;
}
